// Uploads, as the old Space app did them: no file bytes through the API.
//   1. POST /api/nodes/{id}/files {name, size, type}        → {fileId, uploadUrl, headers}
//   2. PUT the file to uploadUrl (Blob Storage; locally the dev server), with those headers
//   3. POST /api/nodes/{id}/files/{fileId}/done, the same body → {file}: now it is in the folder
// The queue lives outside React: an upload goes on when the folder is closed, and the folder shows it
// again when opened. While one runs, leaving the page asks first.
import { readingFileProblem, STRATEGY_LIBRARY, type FileUploadRequest, type FileUploadTicket, type FolderBundle, type FolderFile } from '@pathonline/shared';
import { READINGS_KEY } from '../strategy/inbox.ts';
import type { QueryClient } from '@tanstack/react-query';
import { useMemo, useSyncExternalStore } from 'react';
import { HttpError, queryClient, sendJson } from '../api.ts';
import { fileProblem, putProblem } from './model.ts';

const enc = encodeURIComponent;

/** A PUT the storage refused, or no answer (status 0). */
export class PutError extends Error {
  status: number;
  constructor(status: number) {
    super(putProblem(status));
    this.status = status;
  }
}

/** PUT `file` to `url` with XMLHttpRequest: fetch reports no upload progress. Resolves on 2xx. */
export function putFile(url: string, file: Blob, headers: Record<string, string>, onProgress: (loaded: number) => void, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException('Cancelled', 'AbortError'));
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(file.size);
        resolve();
      } else reject(new PutError(xhr.status));
    };
    xhr.onerror = () => reject(new PutError(0));
    xhr.onabort = () => reject(new DOMException('Cancelled', 'AbortError'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

/** One file on its way. Done ones leave the queue: the folder's list has them then. */
export interface Upload {
  id: string;
  nodeId: string;
  name: string;
  size: number;
  /** Bytes sent so far. */
  loaded: number;
  /** saving: the bytes are there, "done" registers the file; too late to cancel. */
  state: 'queued' | 'uploading' | 'saving' | 'failed';
  error: string;
  /** A failed one may be tried again (not one the checks refused). */
  retry: boolean;
}

interface Job extends Upload {
  file: File;
  abort?: AbortController;
  /** The PUT arrived: Retry only calls "done" again, with the same file id (a repeat answers the saved file). */
  sent?: FileUploadTicket;
}

/** What the queue calls; the app's in `uploads` below, a test's own in the tests. */
export interface UploadDeps {
  ticket: (nodeId: string, f: FileUploadRequest) => Promise<FileUploadTicket>;
  put: typeof putFile;
  done: (nodeId: string, fileId: string, f: FileUploadRequest) => Promise<{ file: FolderFile }>;
  /** A file is in the folder now. */
  saved: (nodeId: string, file: FolderFile) => void;
  /** The node's last upload ended (saved or failed): its folder and icon may change. */
  settled: (nodeId: string) => void;
  /** true while anything is queued or uploading. */
  hold: (busy: boolean) => void;
  /** A new job's id. */
  newId: () => string;
}

/** Files sent at the same time. */
const PARALLEL = 2;
/** Progress redraws at most this often (ms): each one draws the folder again. */
const PROGRESS_EVERY = 200;

export function createUploads(deps: UploadDeps) {
  let jobs: Job[] = [];
  let snapshot: readonly Upload[] = [];
  const listeners = new Set<() => void>();

  const live = (j: Job) => j.state !== 'failed';
  const emit = () => {
    snapshot = jobs.map(({ file: _f, abort: _a, sent: _s, ...u }) => u);
    deps.hold(jobs.some(live));
    for (const l of listeners) l();
  };
  const patch = (id: string, change: Partial<Job>) => {
    jobs = jobs.map((j) => (j.id === id ? { ...j, ...change } : j));
    emit();
  };
  const drop = (id: string) => {
    jobs = jobs.filter((j) => j.id !== id);
    emit();
  };
  const settle = (nodeId: string) => {
    if (!jobs.some((j) => j.nodeId === nodeId && live(j))) deps.settled(nodeId);
  };

  const pump = () => {
    const running = jobs.filter((j) => j.state === 'uploading' || j.state === 'saving').length;
    for (const j of jobs.filter((x) => x.state === 'queued').slice(0, Math.max(0, PARALLEL - running))) void run(j.id);
  };

  async function run(id: string) {
    const job = jobs.find((j) => j.id === id);
    if (!job) return;
    const abort = new AbortController();
    // A retry after the bytes arrived: straight to "done", with the same file id.
    let t = job.sent;
    patch(id, t ? { state: 'saving', loaded: job.size, error: '' } : { state: 'uploading', loaded: 0, error: '', abort });
    // The server trims and normalises the name again; the same body goes with "done".
    const f: FileUploadRequest = { name: job.file.name, size: job.file.size, type: job.file.type };
    try {
      if (!t) {
        const ticket = (t = await deps.ticket(job.nodeId, f));
        if (abort.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
        let at = 0;
        await deps.put(
          ticket.uploadUrl,
          job.file,
          ticket.headers,
          (loaded) => {
            const now = Date.now();
            if (now - at >= PROGRESS_EVERY || loaded >= job.size) {
              at = now;
              patch(id, { loaded });
            }
          },
          abort.signal,
        );
        // Cancelled as the PUT ended: not registered (the nightly sweep removes the blob).
        if (abort.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
        // From here the file is saved: × is gone, so a cancel cannot be ignored silently.
        patch(id, { state: 'saving', loaded: job.size, abort: undefined, sent: ticket });
      }
      const { file } = await deps.done(job.nodeId, t.fileId, f);
      deps.saved(job.nodeId, file);
      drop(id);
    } catch (e) {
      // Cancelled: gone from the list. A blob that arrived is never registered: the nightly sweep removes it.
      if (abort.signal.aborted) drop(id);
      else {
        // 409: the blob is missing or cut off, so Retry starts over from a new ticket. Anything else
        // (no answer, a 5xx: maybe saved already) keeps the file id, and Retry asks "done" again.
        const over = e instanceof HttpError && e.status === 409;
        patch(id, { state: 'failed', error: (e as Error).message || 'The upload failed.', abort: undefined, retry: true, ...(over ? { sent: undefined } : {}) });
      }
    } finally {
      settle(job.nodeId);
      pump();
    }
  }

  return {
    subscribe(l: () => void) {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    get: () => snapshot,
    /** Queues `files` for `nodeId`'s folder. One the checks refuse shows as failed at once. */
    add(nodeId: string, files: readonly File[], maxMb: number) {
      const added = files.map<Job>((file) => {
        const problem = fileProblem(file.name, file.size, maxMb);
        return { id: deps.newId(), nodeId, name: file.name, size: file.size, loaded: 0, state: problem ? 'failed' : 'queued', error: problem, retry: false, file };
      });
      jobs = [...jobs, ...added];
      emit();
      pump();
    },
    /** Stops one: a queued one leaves, a running one is cut off and then leaves. One being saved goes on. */
    cancel(id: string) {
      const j = jobs.find((x) => x.id === id);
      if (!j || j.state === 'saving') return;
      if (j.state === 'uploading') j.abort?.abort();
      else {
        drop(id);
        settle(j.nodeId);
      }
    },
    retry(id: string) {
      const j = jobs.find((x) => x.id === id);
      if (!j || j.state !== 'failed' || !j.retry) return;
      patch(id, { state: 'queued', loaded: 0, error: '' });
      pump();
    },
    /** Takes a failed one off the list. */
    dismiss(id: string) {
      if (jobs.find((x) => x.id === id)?.state === 'failed') drop(id);
    },
  };
}

export type Uploads = ReturnType<typeof createUploads>;

/** Leaving the page would cut off running uploads: the browser asks first. */
const holdPage = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = '';
};

/** The bundle of an open folder, under this key. */
export const folderKey = (nodeId: string) => ['folder', nodeId];

/**
 * A saved file goes into the open folder's list at once. A folder GET on its way is cancelled: its answer
 * may be from before the file. The folder is read again once, with the icons, when the node's last upload ended.
 * No list yet (its first GET on its way): a cancel alone would leave it on "Loading…" until then, so it is read again now.
 */
export const folderCache = (qc: QueryClient): Pick<UploadDeps, 'saved' | 'settled'> => ({
  saved: (id, file) => {
    // A Strategy document of a kind Claude reads joined its queue as it was saved: its library row says so
    // (StrategyPage readingsQuery). Asked as each one is saved: its row offers to queue it only until the answer comes.
    if (id === STRATEGY_LIBRARY && !readingFileProblem(file.name, file.size)) void qc.invalidateQueries({ queryKey: READINGS_KEY });
    const key = folderKey(id);
    if (qc.getQueryData(key) === undefined) {
      void qc.cancelQueries({ queryKey: key }).then(() => qc.refetchQueries({ queryKey: key, type: 'active' }));
      return;
    }
    void qc.cancelQueries({ queryKey: key });
    qc.setQueryData<FolderBundle>(key, (old) => old && { ...old, files: [file, ...old.files.filter((x) => x.id !== file.id)] });
  },
  settled: (id) => {
    void qc.invalidateQueries({ queryKey: folderKey(id) });
    void qc.invalidateQueries({ queryKey: ['views'] });
  },
});

/** The app's queue. */
export const uploads = createUploads({
  ticket: (id, f) => sendJson<FileUploadTicket>('POST', `/api/nodes/${enc(id)}/files`, f),
  put: putFile,
  done: (id, fileId, f) => sendJson<{ file: FolderFile }>('POST', `/api/nodes/${enc(id)}/files/${enc(fileId)}/done`, f),
  ...folderCache(queryClient),
  hold: (busy) => {
    if (typeof window === 'undefined') return;
    if (busy) window.addEventListener('beforeunload', holdPage);
    else window.removeEventListener('beforeunload', holdPage);
  },
  newId: () => crypto.randomUUID(),
});

/** The uploads of one folder, oldest first. */
export function useUploads(nodeId: string, queue: Uploads = uploads): Upload[] {
  const all = useSyncExternalStore(queue.subscribe, queue.get, queue.get);
  return useMemo(() => all.filter((u) => u.nodeId === nodeId), [all, nodeId]);
}
